import { useState, useEffect } from "react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Shield, Lock } from "lucide-react";
import { ExportBar } from "../components/TableExport";

export default function AdminsPage() {
  const { user: currentUser } = useAuth();
  const [admins, setAdmins] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ nom: "", prenom: "", email: "", telephone: "", pin: "" });
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    try {
      const res = await api.get("/users?role=admin");
      setAdmins(res.data);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, role: "admin" };
      if (!payload.pin) delete payload.pin;
      if (selected) {
        await api.put(`/users/${selected.id}`, payload);
        toast.success("Administrateur mis a jour");
      } else {
        await api.post("/users", payload);
        toast.success("Administrateur ajoute");
      }
      setShowAdd(false);
      setSelected(null);
      setForm({ nom: "", prenom: "", email: "", telephone: "", pin: "" });
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const handleDelete = async (id) => {
    if (id === currentUser?.id) {
      toast.error("Vous ne pouvez pas supprimer votre propre compte");
      return;
    }
    if (!window.confirm("Supprimer cet administrateur ?")) return;
    try {
      await api.delete(`/users/${id}`);
      toast.success("Administrateur supprime");
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const openEdit = (a) => {
    setSelected(a);
    setForm({ nom: a.nom, prenom: a.prenom, email: a.email || "", telephone: a.telephone || "", pin: "" });
    setShowAdd(true);
  };

  const adminExportCols = [
    { label: "Prenom", key: "prenom" },
    { label: "Nom", key: "nom" },
    { label: "Email", key: "email" },
    { label: "Telephone", key: "telephone" },
    { label: "PIN", accessor: a => a.has_pin ? "Configure" : "Non defini" },
  ];

  if (loading) return <div className="text-slate-500">Chargement...</div>;

  return (
    <div data-testid="admins-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Administrateurs</h1>
          <p className="text-sm text-slate-500 mt-1">{admins.length} administrateur(s)</p>
        </div>
        <Button className="bg-[#0F52BA] hover:bg-[#0A3D8F]" onClick={() => { setSelected(null); setForm({ nom: "", prenom: "", email: "", telephone: "", pin: "" }); setShowAdd(true); }} data-testid="btn-add-admin">
          <Plus className="w-4 h-4 mr-2" />Ajouter
        </Button>
      </div>

      <ExportBar
        searchTerm="" onSearchChange={null} searchPlaceholder={null}
        filterConfigs={[]} filters={{}} onFilterChange={() => {}} onReset={() => {}} hasActiveFilters={false}
        data={admins} columns={adminExportCols} filename="administrateurs" title="Liste des administrateurs"
      />

      <div className="table-container">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50/50">
              <TableHead className="font-semibold text-slate-600">Nom</TableHead>
              <TableHead className="font-semibold text-slate-600">Email</TableHead>
              <TableHead className="font-semibold text-slate-600">Telephone</TableHead>
              <TableHead className="font-semibold text-slate-600">Code PIN</TableHead>
              <TableHead className="font-semibold text-slate-600">Statut</TableHead>
              <TableHead className="font-semibold text-slate-600 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {admins.map(a => (
              <TableRow key={a.id} data-testid={`admin-row-${a.id}`}>
                <TableCell className="font-medium text-slate-900">
                  {a.prenom} {a.nom}
                  {a.id === currentUser?.id && <Badge className="ml-2 bg-blue-100 text-blue-700 border-blue-200 text-[10px]">Vous</Badge>}
                </TableCell>
                <TableCell className="text-slate-600">{a.email || "-"}</TableCell>
                <TableCell className="text-slate-600">{a.telephone || "-"}</TableCell>
                <TableCell>
                  {a.has_pin ? (
                    <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200"><Lock className="w-3 h-3 mr-1" />Configure</Badge>
                  ) : (
                    <span className="text-slate-400 text-sm">Non defini</span>
                  )}
                </TableCell>
                <TableCell><Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">Actif</Badge></TableCell>
                <TableCell className="text-right">
                  <div className="flex gap-1 justify-end">
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(a)} data-testid={`btn-edit-admin-${a.id}`}>
                      <Pencil className="w-4 h-4 text-slate-500" />
                    </Button>
                    {a.id !== currentUser?.id && (
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleDelete(a.id)} data-testid={`btn-delete-admin-${a.id}`}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {admins.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-slate-500 py-12">
                  <Shield className="w-10 h-10 mx-auto mb-3 text-slate-300" />
                  Aucun administrateur.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Dialog */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent data-testid="admin-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{selected ? "Modifier l'administrateur" : "Ajouter un administrateur"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Prenom</Label>
                <Input value={form.prenom} onChange={e => setForm({ ...form, prenom: e.target.value })} required data-testid="input-admin-prenom" />
              </div>
              <div>
                <Label>Nom</Label>
                <Input value={form.nom} onChange={e => setForm({ ...form, nom: e.target.value })} required data-testid="input-admin-nom" />
              </div>
              <div>
                <Label>Email</Label>
                <Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} data-testid="input-admin-email" />
              </div>
              <div>
                <Label>Telephone</Label>
                <Input value={form.telephone} onChange={e => setForm({ ...form, telephone: e.target.value })} data-testid="input-admin-telephone" />
              </div>
            </div>
            <div>
              <Label>Code PIN <span className="text-xs text-slate-400 font-normal">(4 ou 6 chiffres, optionnel{selected ? " - laisser vide pour ne pas modifier" : ""})</span></Label>
              <Input
                type="password"
                placeholder={selected ? "Laisser vide pour conserver l'actuel" : "Ex: 1234 ou 123456"}
                maxLength={6}
                value={form.pin}
                onChange={e => setForm({ ...form, pin: e.target.value.replace(/\D/g, "") })}
                data-testid="input-admin-pin"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setShowAdd(false)}>Annuler</Button>
              <Button type="submit" className="bg-[#0F52BA] hover:bg-[#0A3D8F]" data-testid="btn-submit-admin">
                {selected ? "Modifier" : "Ajouter"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
